/**
 * Temporary table components used while Import Hub is being migrated to AG Grid.
 * TODO: Remove them after all tables in this microfrontend have moved to AG Grid.
 */
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type CSSProperties,
} from 'react';

import { Skeleton, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

export type TableContainerProps = ComponentPropsWithoutRef<'div'>;

export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'relative w-full overflow-auto rounded-lg border border-border-secondary',
        className,
      )}
      {...props}
    />
  ),
);
TableContainer.displayName = 'TableContainer';

export type TableProps = ComponentPropsWithoutRef<'table'> & {
  stickyHeader?: boolean;
};

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, stickyHeader, ...props }, ref) => (
    <table
      ref={ref}
      className={cn(
        'w-full border-separate border-spacing-0 bg-ui-primary',
        typographyVariants({ variant: 'bodyShort01', color: 'primary' }),
        stickyHeader && '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-1',
        className,
      )}
      {...props}
    />
  ),
);
Table.displayName = 'Table';

export type TableHeadProps = ComponentPropsWithoutRef<'thead'>;

export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('bg-ui-primary', className)} {...props} />
  ),
);
TableHead.displayName = 'TableHead';

export type TableBodyProps = ComponentPropsWithoutRef<'tbody'>;

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={className} {...props} />
  ),
);
TableBody.displayName = 'TableBody';

export type TableRowProps = ComponentPropsWithoutRef<'tr'> & {
  hover?: boolean;
  selected?: boolean;
};

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, hover, selected, ...props }, ref) => (
    <tr
      ref={ref}
      aria-selected={selected || undefined}
      className={cn(
        'group/table-row bg-ui-primary',
        hover && 'hover:bg-ui-hover',
        selected && 'bg-ui-selected',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

type TableCellAlignment = 'center' | 'left' | 'right';
type TableCellLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type TableCellSkeletonVariant = 'avatar' | 'checkbox' | 'default';

const alignmentClasses: Record<TableCellAlignment, string> = {
  center: 'justify-center text-center',
  left: 'justify-start text-left',
  right: 'justify-end text-right',
};

const bodyCellColorClasses = {
  default: 'bg-ui-primary',
  error: 'bg-error-main/8',
  editable: 'bg-ui-hoverCell',
  'hover-editable': 'bg-ui-primary group-hover/table-row:bg-ui-hoverCell',
  success: 'bg-success-main/8',
  warning: 'bg-warning-main/8',
} as const;

export type TableBodyCellProps = Omit<
  ComponentPropsWithoutRef<'td'>,
  'align'
> & {
  align?: TableCellAlignment;
  baseline?: boolean;
  bgColor?: keyof typeof bodyCellColorClasses;
  isLoading?: boolean;
  level?: TableCellLevel;
  levelIsLast?: boolean;
  skeletonVariant?: TableCellSkeletonVariant;
};

export const TableBodyCell = forwardRef<
  HTMLTableCellElement,
  TableBodyCellProps
>(
  (
    {
      align = 'left',
      baseline = false,
      bgColor = 'default',
      children,
      className,
      isLoading = false,
      level = 0,
      levelIsLast = false,
      skeletonVariant = 'default',
      style,
      ...props
    },
    ref,
  ) => (
    <td
      ref={ref}
      className={cn(
        'relative box-border min-h-10 border-t border-border-secondary px-4 align-middle',
        bodyCellColorClasses[bgColor],
        className,
      )}
      style={
        {
          '--table-cell-level': `${level}rem`,
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      <div
        className={cn(
          'flex min-h-10 w-full gap-2',
          alignmentClasses[align],
          baseline ? 'items-baseline pt-[11px]' : 'items-center',
        )}
      >
        {isLoading ? (
          <Skeleton
            variant={skeletonVariant === 'avatar' ? 'circle' : 'rounded'}
            className={cn(
              skeletonVariant === 'default' && 'h-4 w-full',
              skeletonVariant === 'checkbox' && 'size-3.5',
              skeletonVariant === 'avatar' && 'size-6',
            )}
          />
        ) : (
          <>
            {level > 0 && (
              <span
                aria-hidden
                className={cn(
                  'table-cell-level h-px w-(--table-cell-level) shrink-0 bg-ui-tertiary',
                  levelIsLast && 'self-start',
                )}
              />
            )}
            {children}
          </>
        )}
      </div>
    </td>
  ),
);
TableBodyCell.displayName = 'TableBodyCell';

export type TableHeaderCellProps = Omit<
  ComponentPropsWithoutRef<'th'>,
  'align'
> & {
  align?: TableCellAlignment;
  skeletonVariant?: TableCellSkeletonVariant;
  subheader?: boolean;
};

export const TableHeaderCell = forwardRef<
  HTMLTableCellElement,
  TableHeaderCellProps
>(
  (
    {
      align = 'left',
      children,
      className,
      skeletonVariant: _skeletonVariant,
      subheader = false,
      ...props
    },
    ref,
  ) => (
    <th
      ref={ref}
      scope="col"
      className={cn(
        'box-border h-10 px-4 align-middle',
        typographyVariants({ variant: 'h6', color: 'primary' }),
        subheader
          ? 'border-t border-border-secondary bg-ui-secondary'
          : 'bg-ui-primary',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'flex min-w-0 items-center gap-2',
          alignmentClasses[align],
        )}
      >
        {children}
      </div>
    </th>
  ),
);
TableHeaderCell.displayName = 'TableHeaderCell';
